import React from 'react';
import type { ExpenseCategory, ExpenseMethod } from '@/types/ExpenseCategory';
import { IRS_MILEAGE_RATE } from '@/types/ExpenseCategory';
import { getPredefinedDefinition } from '@/constants/expenses';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ExpenseCategoryRowProps {
  category: ExpenseCategory;
  onChange: (updated: ExpenseCategory) => void;
  onDelete?: () => void;
  showScope?: boolean;
}

const METHOD_LABELS: Record<ExpenseMethod, string> = {
  at_cost: 'At Cost',
  standard_rate: 'Standard Rate',
  client_provided: 'Client Provided',
  not_applicable: 'Not Applicable',
};

const METHOD_DESCRIPTIONS: Record<ExpenseMethod, string> = {
  at_cost: 'Reimbursed with receipt',
  standard_rate: 'Official rate (IRS/GSA)',
  client_provided: 'Client pays directly',
  not_applicable: 'Disabled',
};

export const ExpenseCategoryRow: React.FC<ExpenseCategoryRowProps> = ({
  category,
  onChange,
  onDelete,
  showScope = true,
}) => {
  const definition = getPredefinedDefinition(category.id);
  const hasStandardRate = definition?.standardRateAvailable != null;

  const availableMethods: ExpenseMethod[] = ['at_cost', 'client_provided', 'not_applicable'];
  if (hasStandardRate) {
    availableMethods.splice(1, 0, 'standard_rate');
  }

  const handleMethodChange = (method: ExpenseMethod) => {
    const updates: Partial<ExpenseCategory> = { method };
    if (method === 'standard_rate' && definition?.standardRateAvailable) {
      updates.standardRateType = definition.standardRateAvailable;
      if (definition.standardRateAvailable === 'irs_mileage') {
        updates.rate = IRS_MILEAGE_RATE;
      }
    } else {
      updates.standardRateType = undefined;
      if (method !== 'at_cost') {
        updates.rate = null;
      }
    }
    onChange({ ...category, ...updates });
  };

  const showRateInput = category.method === 'at_cost' || category.method === 'standard_rate';

  return (
    <div className="flex flex-col gap-3 p-3 rounded border bg-surface-1">
      {/* Row header: name + scope + delete */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-foreground">
            {category.name || 'Unnamed Category'}
          </Label>
          {showScope && (
            <Badge variant="outline" className="text-xs">
              {category.scope === 'all' ? 'All Bookings' : 'Travel Only'}
            </Badge>
          )}
        </div>
        {category.isCustom && onDelete && (
          <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Method selector */}
      <div className="flex items-center gap-3">
        <Label className="text-xs text-muted-foreground w-16 shrink-0">Method</Label>
        <Select value={category.method} onValueChange={(v) => handleMethodChange(v as ExpenseMethod)}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableMethods.map((m) => (
              <SelectItem key={m} value={m} className="text-xs">
                <div>
                  <span className="font-medium">{METHOD_LABELS[m]}</span>
                  <span className="ml-2 text-muted-foreground">{METHOD_DESCRIPTIONS[m]}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Rate input — shown for at_cost and standard_rate */}
      <AnimatedCollapse open={showRateInput}>
            <div className="flex items-center gap-3">
              <Label className="text-xs text-muted-foreground w-16 shrink-0">
                {category.method === 'standard_rate'
                  ? category.standardRateType === 'irs_mileage' ? 'IRS Rate' : 'GSA Rate'
                  : 'Rate'}
              </Label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">$</span>
                <input
                  type="number"
                  value={category.rate ?? ''}
                  onChange={(e) => onChange({ ...category, rate: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder={category.method === 'standard_rate' ? 'Auto' : 'Optional'}
                  disabled={category.method === 'standard_rate' && category.standardRateType === 'irs_mileage'}
                  className={cn(
                    "h-8 w-24 rounded border bg-surface-2 px-2 text-right text-xs",
                    "focus:outline-none focus:ring-1 focus:ring-primary",
                    category.method === 'standard_rate' && category.standardRateType === 'irs_mileage' && "opacity-60"
                  )}
                  step={0.01}
                  min={0}
                />
                {category.standardRateType === 'irs_mileage' && (
                  <span className="text-xs text-muted-foreground">/mi</span>
                )}
                {category.standardRateType === 'gsa_lodging' && (
                  <span className="text-xs text-muted-foreground">/night</span>
                )}
              </div>
            </div>
      </AnimatedCollapse>
    </div>
  );
};
